/**
 * Δέντρο κατηγοριών › υποκατηγοριών (ένα επίπεδο, όπως σήμερα). Σύρσιμο: πάνω/κάτω από άλλη =
 * σειρά, πάνω ΣΕ κατηγορία = γίνεται υποκατηγορία της· τσεκαρισμένα προϊόντα πάνω σε κατηγορία =
 * μετακίνηση. Με το πληκτρολόγιο: Alt+↑/↓ αλλάζει σειρά.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown, ChevronRight, EyeOff, GripVertical, Inbox, Layers, PanelLeftClose, PanelLeftOpen, Pencil, Plus, X,
} from 'lucide-react';
import { cx, ToneDot } from './ui';
import type { TreeNodeView } from './catalogView';
import type { DragState } from './dragState';

export interface CatalogTreeProps {
  nodes: TreeNodeView[];
  allCount: number;
  noneCount: number;
  selected: string;
  expanded: Set<string>;
  edited: Set<string>;
  readOnly: boolean;
  lockTitle?: string;
  variant: 'pane' | 'drawer';
  drag: DragState;
  onSelect: (key: string) => void;
  onToggle: (key: string) => void;
  onEdit: (key: string) => void;
  onAdd: () => void;
  onCollapse?: () => void;
  onPin?: () => void;
  onClose?: () => void;
  onMoveCategory: (key: string, parentKey: string | null, index: number) => void;
  onDropProducts: (keys: string[], node: string) => void;
}

type DropPos = 'before' | 'after' | 'inside';

const CatalogTree: React.FC<CatalogTreeProps> = props => {
  const { t } = useTranslation(['catalog']);
  const { nodes, selected, expanded, readOnly, drag } = props;
  const [over, setOver] = useState<{ key: string; pos: DropPos } | null>(null);

  const find = (key: string): { node: TreeNodeView; parent: TreeNodeView | null; index: number } | null => {
    for (let i = 0; i < nodes.length; i += 1) {
      if (nodes[i].key === key) return { node: nodes[i], parent: null, index: i };
      const j = nodes[i].children.findIndex(c => c.key === key);
      if (j >= 0) return { node: nodes[i].children[j], parent: nodes[i], index: j };
    }
    return null;
  };

  /** Πού πέφτει μια κατηγορία πάνω στον κόμβο target (null = δεν επιτρέπεται). */
  const categoryDrop = (dragKey: string, targetKey: string, pos: DropPos): { parent: string | null; index: number } | null => {
    if (dragKey === targetKey) return null;
    const src = find(dragKey);
    const dst = find(targetKey);
    if (!src || !dst) return null;
    const hasKids = src.node.children.length > 0;
    if (pos === 'inside') {
      // Ένα επίπεδο: μόνο σε ρίζα, και όχι κατηγορία που έχει ήδη υποκατηγορίες.
      if (dst.parent || hasKids) return null;
      return { parent: dst.node.key, index: dst.node.children.filter(c => c.key !== dragKey).length };
    }
    if (dst.parent && hasKids) return null;
    const siblings = dst.parent ? dst.parent.children : nodes;
    const without = siblings.filter(n => n.key !== dragKey);
    const at = without.findIndex(n => n.key === targetKey);
    return { parent: dst.parent ? dst.parent.key : null, index: pos === 'before' ? at : at + 1 };
  };

  const posOf = (e: React.DragEvent, allowInside: boolean): DropPos => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    if (!rect.height) return allowInside ? 'inside' : 'after';
    const y = (e.clientY - rect.top) / rect.height;
    if (allowInside && y > 0.28 && y < 0.72) return 'inside';
    return y < 0.5 ? 'before' : 'after';
  };

  const onDragOver = (e: React.DragEvent, key: string, isRoot: boolean) => {
    const d = drag.current;
    if (!d || readOnly) return;
    if (d.kind === 'products') {
      e.preventDefault();
      setOver({ key, pos: 'inside' });
      return;
    }
    if (d.kind !== 'category') return;
    const pos = posOf(e, isRoot);
    if (!categoryDrop(d.key, key, pos)) { setOver(null); return; }
    e.preventDefault();
    setOver({ key, pos });
  };

  const onDrop = (e: React.DragEvent, key: string, isRoot: boolean) => {
    e.preventDefault();
    const d = drag.current;
    setOver(null);
    drag.end();
    if (!d || readOnly) return;
    if (d.kind === 'products') { props.onDropProducts(d.keys, key); return; }
    if (d.kind !== 'category') return;
    const target = categoryDrop(d.key, key, over && over.key === key ? over.pos : posOf(e, isRoot));
    if (target) props.onMoveCategory(d.key, target.parent, target.index);
  };

  /** Βελάκια ανάμεσα στους ορατούς κόμβους (όπως ένα δέντρο αρχείων). */
  const focusMove = (e: React.KeyboardEvent, step: number | 'first' | 'last') => {
    const items = Array.from((e.currentTarget.closest('[role="tree"]') as HTMLElement).querySelectorAll<HTMLElement>('[role="treeitem"]'));
    const at = items.indexOf(e.currentTarget as HTMLElement);
    const next = step === 'first' ? 0 : step === 'last' ? items.length - 1 : at + step;
    if (next >= 0 && next < items.length) { e.preventDefault(); items[next].focus(); }
  };

  const onNavKey = (e: React.KeyboardEvent, key: string): boolean => {
    if (e.altKey) return false;
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); props.onSelect(key); return true; }
    if (e.key === 'ArrowDown') { focusMove(e, 1); return true; }
    if (e.key === 'ArrowUp') { focusMove(e, -1); return true; }
    if (e.key === 'Home') { focusMove(e, 'first'); return true; }
    if (e.key === 'End') { focusMove(e, 'last'); return true; }
    const at = find(key);
    if (!at) return false;
    if (e.key === 'ArrowRight' && at.node.children.length > 0) {
      e.preventDefault();
      if (!expanded.has(key)) props.onToggle(key); else focusMove(e, 1);
      return true;
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      if (at.node.children.length > 0 && expanded.has(key)) props.onToggle(key);
      else if (at.parent) {
        const tree = e.currentTarget.closest('[role="tree"]') as HTMLElement;
        tree.querySelector<HTMLElement>(`[data-key="${at.parent.key}"]`)?.focus();
      }
      return true;
    }
    return false;
  };

  const onKeyDown = (e: React.KeyboardEvent, key: string) => {
    if (onNavKey(e, key)) return;
    if (readOnly || !e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;
    const at = find(key);
    if (!at) return;
    e.preventDefault();
    const siblings = at.parent ? at.parent.children : nodes;
    const next = at.index + (e.key === 'ArrowUp' ? -1 : 1);
    if (next < 0 || next >= siblings.length) return;
    props.onMoveCategory(key, at.parent ? at.parent.key : null, next);
  };

  const row = (n: TreeNodeView, level: 1 | 2, isRoot: boolean) => {
    const sel = selected === n.key;
    const hasKids = n.children.length > 0;
    const open = expanded.has(n.key);
    const drop = over && over.key === n.key ? over.pos : null;
    return (
      <div
        key={n.key}
        role="treeitem"
        data-key={n.key}
        aria-level={level}
        aria-selected={sel}
        aria-expanded={hasKids ? open : undefined}
        aria-label={n.name}
        tabIndex={sel ? 0 : -1}
        title={n.hidden ? t('catalog:tree.hiddenTitle', { name: n.name }) : n.name}
        className={cx('cx-node', `l${level}`, sel && 'sel', n.hidden && 'is-hidden', drop && `drop-${drop}`)}
        draggable={!readOnly}
        onDragStart={e => {
          if (readOnly) return;
          drag.start({ kind: 'category', key: n.key });
          try { e.dataTransfer.setData('text/plain', n.key); e.dataTransfer.effectAllowed = 'move'; } catch { /* jsdom */ }
        }}
        onDragEnd={() => { drag.end(); setOver(null); }}
        onDragOver={e => onDragOver(e, n.key, isRoot)}
        onDragLeave={() => setOver(o => (o && o.key === n.key ? null : o))}
        onDrop={e => onDrop(e, n.key, isRoot)}
        onClick={() => props.onSelect(n.key)}
        onKeyDown={e => onKeyDown(e, n.key)}
      >
        {!readOnly && <span className="cx-grip" aria-hidden="true"><GripVertical className="cx-ico" /></span>}
        {level === 1 && (
          hasKids ? (
            <button
              type="button"
              className="cx-chev"
              aria-label={open ? t('catalog:tree.collapse', { name: n.name }) : t('catalog:tree.expand', { name: n.name })}
              onClick={e => { e.stopPropagation(); props.onToggle(n.key); }}
            >
              {open ? <ChevronDown className="cx-ico" /> : <ChevronRight className="cx-ico" />}
            </button>
          ) : <span className="cx-chev" aria-hidden="true" />
        )}
        <ToneDot tone={n.tone} />
        <span className="cx-name">{n.name || t('catalog:category.untitled')}</span>
        {props.edited.has(n.key) && <span className="cx-edot" title={t('catalog:list.unsaved')} />}
        {n.hidden && <span className="cx-hid" aria-label={t('catalog:tree.hidden')}><EyeOff className="cx-ico" /></span>}
        <span className="cx-count">{n.count}</span>
        <button
          type="button"
          className="cx-edit"
          aria-label={t('catalog:tree.edit', { name: n.name })}
          title={t('catalog:tree.edit', { name: n.name })}
          onClick={e => { e.stopPropagation(); props.onEdit(n.key); }}
        >
          <Pencil className="cx-ico" />
        </button>
      </div>
    );
  };

  const special = (key: 'all' | 'none', icon: React.ReactNode, label: string, count: number) => (
    <div
      role="treeitem"
      aria-level={1}
      aria-selected={selected === key}
      aria-label={label}
      tabIndex={selected === key ? 0 : -1}
      className={cx('cx-node', selected === key && 'sel', over && over.key === key && 'drop-in')}
      onClick={() => props.onSelect(key)}
      onKeyDown={e => { onNavKey(e, key); }}
      onDragOver={key === 'none' ? e => { if (drag.current?.kind === 'products' && !readOnly) { e.preventDefault(); setOver({ key, pos: 'inside' }); } } : undefined}
      onDragLeave={key === 'none' ? () => setOver(o => (o && o.key === key ? null : o)) : undefined}
      onDrop={key === 'none' ? e => { e.preventDefault(); const d = drag.current; setOver(null); drag.end(); if (d?.kind === 'products' && !readOnly) props.onDropProducts(d.keys, 'none'); } : undefined}
    >
      <span className="cx-lead" aria-hidden="true">{icon}</span>
      <span className="cx-name">{label}</span>
      <span className="cx-count">{count}</span>
    </div>
  );

  const body = (
    <div className="cx-tree-body" role="tree" aria-label={t('catalog:tree.title')}>
      {special('all', <Layers className="cx-ico" />, t('catalog:tree.all'), props.allCount)}
      <div className="cx-tree-sep" />
      {nodes.map(n => (
        <React.Fragment key={n.key}>
          {row(n, 1, true)}
          {n.children.length > 0 && expanded.has(n.key) && (
            <div className="cx-subs" role="group">{n.children.map(c => row(c, 2, false))}</div>
          )}
        </React.Fragment>
      ))}
      {(props.noneCount > 0 || selected === 'none') && (
        <>
          <div className="cx-tree-sep" />
          {special('none', <Inbox className="cx-ico" />, t('catalog:tree.none'), props.noneCount)}
        </>
      )}
    </div>
  );

  const head = (
    <div className="cx-tree-head">
      <span className="cx-t">{t('catalog:tree.title')}</span>
      <span className="cx-tc">{nodes.length}</span>
      <span className="cx-sp" />
      <button type="button" className="cx-icon-btn sm" onClick={props.onAdd} disabled={readOnly}
        aria-label={t('catalog:tree.add')} title={readOnly ? props.lockTitle : t('catalog:tree.add')}>
        <Plus className="cx-ico" />
      </button>
      {props.variant === 'pane' && props.onCollapse && (
        <button type="button" className="cx-icon-btn sm" onClick={props.onCollapse} aria-label={t('catalog:tree.collapseTree')} title={t('catalog:tree.collapseTree')}>
          <PanelLeftClose className="cx-ico" />
        </button>
      )}
      {props.variant === 'drawer' && props.onPin && (
        <button type="button" className="cx-icon-btn sm" onClick={props.onPin} aria-label={t('catalog:tree.pin')} title={t('catalog:tree.pin')}>
          <PanelLeftOpen className="cx-ico" />
        </button>
      )}
      {props.variant === 'drawer' && props.onClose && (
        <button type="button" className="cx-icon-btn sm" onClick={props.onClose} aria-label={t('catalog:common.close')} title={t('catalog:common.close')}>
          <X className="cx-ico" />
        </button>
      )}
    </div>
  );

  return (
    <section className={cx('cx-pane', props.variant === 'drawer' && 'cx-drawer')} aria-label={t('catalog:tree.title')}>
      {head}
      {body}
      {props.variant === 'pane' && (
        <div className="cx-tree-foot">
          <button type="button" className="cx-btn ghost sm" onClick={props.onAdd} disabled={readOnly} title={readOnly ? props.lockTitle : undefined}>
            <Plus className="cx-ico" /> {t('catalog:tree.addShort')}
          </button>
          {!readOnly && <p className="cx-hint" title={t('catalog:tree.dragHintLong')}>{t('catalog:tree.dragHint')}</p>}
        </div>
      )}
    </section>
  );
};

/** Λωρίδα όταν το δέντρο είναι μαζεμένο (κάτω από ~1320 px με ανοιχτό πάνελ). */
export const TreeRail: React.FC<{ onOpen: () => void }> = ({ onOpen }) => {
  const { t } = useTranslation(['catalog']);
  return (
    <button type="button" className="cx-pane cx-rail" onClick={onOpen} aria-label={t('catalog:tree.openTree')} title={t('catalog:tree.openTree')}>
      <span className="cx-icon-btn sm" aria-hidden="true"><PanelLeftOpen className="cx-ico" /></span>
      <span className="cx-rail-label">{t('catalog:tree.title')}</span>
    </button>
  );
};

export default CatalogTree;
